import { ArchivedCompanyNotice } from '@/components/archived-company-notice';
import { MatrixBreadcrumb } from '@/components/breadcrumbs';
import { CadragePanel } from '@/components/matrix/cadrage-panel';
import {
  type MatrixViewSearch,
  cadrageRedirect,
  matrixViewSearchSchema,
} from '@/components/matrix/cadrage-view';
import { ContextStaleBanner } from '@/components/matrix/context-stale-banner';
import { staleCellIds } from '@/components/matrix/context-staleness';
import { isEditableElement, matchUndoRedoShortcut } from '@/components/matrix/keyboard-shortcuts';
import { type DrawerNotes, MatrixDrawer } from '@/components/matrix/matrix-drawer';
import { MatrixGrid } from '@/components/matrix/matrix-grid';
import { MatrixToolbar } from '@/components/matrix/matrix-toolbar';
import {
  type GridNotes,
  type NotesRequest,
  findNoteAnchor,
  latestOpenByTarget,
  noteTargetLabel,
  noteTargetToSelection,
  selectionToNoteTarget,
  totalOpenCount,
} from '@/components/matrix/note-targets';
import { NotesBubble } from '@/components/matrix/notes-bubble';
import {
  type DrawerRequest,
  type DrawerTab,
  MATRIX_SELECTION,
  type Selection,
  cellsInScope,
} from '@/components/matrix/selection';
import { type ZoomStep, clampZoomStep } from '@/components/matrix/zoom';
import { NotesView } from '@/components/notes-view/notes-view';
import type { NotesViewSearch } from '@/components/notes-view/notes-view-search';
import { NotesTargetPanel } from '@/components/notes/notes-target-panel';
import {
  ApiError,
  cellsApi,
  companiesApi,
  dimensionGenerationApi,
  matricesApi,
  matrixRegenerationApi,
  meApi,
} from '@/lib/api';
import { canManageCompanies } from '@/lib/company-permissions';
import { useEffectiveRole } from '@/lib/effective-role';
import { matrixDisplayState } from '@/lib/matrix-grid-search';
import { setNoteMarkersHidden, useNoteMarkersHidden } from '@/lib/matrix-notes-prefs-store';
import { setMatrixViewMode, useMatrixViewMode } from '@/lib/matrix-view-mode-store';
import {
  type NoteViewer,
  type NotesScope,
  openCountsByTarget,
  targetKey,
  useMatrixNotes,
} from '@/lib/notes-queries';
import { useDeriveMatrix } from '@/lib/use-derive-matrix';
import { cn } from '@/lib/utils';
import type {
  Cell,
  Matrix,
  MatrixViewStateShape,
  MatrixWithDimensions,
  NoteTarget,
  PerformResizeInput,
  PerformVisibilityInput,
  RegenerationScopeSelector,
} from '@mss_creator/shared';
import { DEFAULT_MATRIX_VIEW_STATE } from '@mss_creator/shared';
import { Button, useToast } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/matrices/$matrixId')({
  /**
   * `?vue=cadrage` (issue #3) — a search parameter on this route, not a child
   * route, so `MatrixGrid` stays mounted behind it. See `cadrage-view.ts` for
   * the schema and the legacy-link rule; the pure parts live there because a
   * route module needs a live router context to render and is excluded from
   * coverage.
   */
  validateSearch: (search): MatrixViewSearch => matrixViewSearchSchema.parse(search),
  beforeLoad: ({ search, params }) => {
    const target = cadrageRedirect(search);
    if (target) {
      throw redirect({
        to: '/matrices/$matrixId',
        params: { matrixId: params.matrixId },
        search: target,
        replace: true,
      });
    }
  },
  loader: async ({ params }): Promise<MatrixWithDimensions> => matricesApi.get(params.matrixId),
  component: MatrixRoutePage,
  pendingComponent: PendingScreen,
  errorComponent: ErrorScreen,
  staticData: { breadcrumb: MatrixBreadcrumb },
});

/**
 * Turns a failed `POST /matrices/:id/dimensions/generation-jobs` (point 14,
 * fix round 1) into a short French message for `AddDimensionButton` —
 * the popover's own disabled-submit gate already guarantees non-empty
 * title/comment, so a request that still fails here is a transport/server
 * error (404 unknown matrix, 422 matrix not `généré`/no credential, 409
 * `DIMENSION_GENERATION_IN_PROGRESS`, 500, network drop), never a validation
 * one. `AddDimensionButton` keeps the popover open with this message (and
 * the user's typed input intact) rather than silently discarding the
 * attempt.
 */
function toDimensionSubmitError(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'Une génération de dimension est déjà en cours pour cette matrice.';
  }
  return 'Impossible de créer la dimension. Réessayez.';
}

/**
 * Shown when a view-state write fails. Deliberately generic: the user can't
 * act on the distinction between a 409, a 500 and a dropped connection, and
 * backend messages are English while this UI is French-only. The point is
 * that a failure is *visible at all* — zoom silently 409'd for the whole life
 * of the feature because this mutation had no error handler.
 */
const VIEW_STATE_ERROR_MESSAGE = "L'affichage n'a pas pu être enregistré. Réessayez.";

/**
 * Maps a regeneration batch's stored scope selector onto the grid's own
 * `Selection` shape, so `cellsInScope` can resolve "which cells did this
 * batch touch" the same way it resolves the current grid selection.
 */
function scopeSelectorToSelection(selector: RegenerationScopeSelector): Selection {
  switch (selector.scope) {
    case 'matrix':
      return { scope: 'matrix' };
    case 'activationBlock':
      return { scope: 'activationBlock' };
    case 'row':
      return { scope: 'row', rowType: selector.rowType, rowRef: selector.rowRef };
    case 'column':
      return { scope: 'column', columnNumber: selector.columnNumber };
    case 'cell':
      return { scope: 'cell', cellId: selector.cellId };
    case 'multiCell':
      return { scope: 'cells', cellIds: selector.cellIds };
    default:
      return selector satisfies never;
  }
}

/**
 * What the route renders: the matrix page, or — when its company is archived
 * (issue #20) — the notice that closes it, with « Restaurer » for the roles
 * that may. The company is read before anything else is drawn so that a closed
 * matrix never flashes on screen.
 */
function MatrixRoutePage() {
  const loaderMatrix = Route.useLoaderData();
  const companyQuery = useQuery({
    queryKey: ['company', loaderMatrix.companyId],
    queryFn: () => companiesApi.getById(loaderMatrix.companyId),
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: meApi.get });
  const effectiveRole = useEffectiveRole(meQuery.data);

  if (companyQuery.isPending) {
    return <PendingScreen />;
  }
  if (companyQuery.data?.archivedAt) {
    return (
      <ArchivedCompanyNotice
        companyId={loaderMatrix.companyId}
        canRestore={canManageCompanies(effectiveRole)}
      />
    );
  }
  return <MatrixGenerationPage />;
}

/**
 * The matrix generation display page: page chrome (title row, M14 — the
 * breadcrumb above it is BUILD-770's centralized <Breadcrumbs>), a toolbar
 * (undo/redo, zoom %, "tout afficher", reset sizes,
 * multi-select toggle, inert export — M12/M13), the 8-palier-column × N-row
 * grid, and the collapsible right-side drawer, laid out as a real flex row
 * (E10) rather than a `fixed` overlay.
 *
 * Selection, multi-select mode, inline-edit state and the drawer's open/tab
 * state are ephemeral, client-only React state (decision D9) — never
 * persisted to `matrices.viewState` and reset on every reload. `viewState`
 * itself (widths, hidden columns/rows, zoom, activation-row collapse) IS
 * shared, server-persisted state (E2) — see `matrixQuery` below.
 */
function MatrixGenerationPage() {
  const loaderMatrix = Route.useLoaderData();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /**
   * Whether the Cadrage tab is showing. Read straight from the URL rather than
   * mirrored into React state: the URL is the only copy, so a deep link, the
   * back button and the toolbar toggle cannot disagree about which view is up.
   */
  const cadrageOpen = search.vue === 'cadrage';
  /** Same for the Notes view (issue #19): the grid stays mounted behind it, the drawer is unmounted. */
  const notesViewOpen = search.vue === 'notes';
  const viewOpen = cadrageOpen || notesViewOpen;

  const matrixQuery = useQuery({
    queryKey: ['matrix', loaderMatrix.id],
    queryFn: () => matricesApi.get(loaderMatrix.id),
    initialData: loaderMatrix,
  });
  const matrix = matrixQuery.data;
  const isGenerated = matrix.state === 'généré';

  // This route only ever renders the généré grid now — brouillon and
  // génération_en_cours both live in the wizard's locked récapitulatif
  // (Task 14). A non-généré matrix reaching this URL directly (a stale
  // bookmark, a manual navigation) redirects there instead of rendering
  // anything here.
  useEffect(() => {
    if (!isGenerated) {
      void navigate({ to: '/wizard/$matrixId', params: { matrixId: matrix.id }, replace: true });
    }
  }, [isGenerated, matrix.id, navigate]);

  const companyQuery = useQuery({
    queryKey: ['company', matrix.companyId],
    queryFn: () => companiesApi.getById(matrix.companyId),
    enabled: isGenerated,
  });
  const cellsQuery = useQuery({
    queryKey: ['cells', matrix.id],
    queryFn: () => cellsApi.listForMatrix(matrix.id),
    enabled: isGenerated,
  });
  // Only decides whether the Cadrage tab renders its Prompt section at all.
  // `GET /matrices/:id/generation-prompts` does the enforcing (403) — repo
  // convention is a backend gate plus a hidden entry point, never a frontend
  // route guard.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: meApi.get });
  const effectiveRole = useEffectiveRole(meQuery.data);

  const [selection, setSelection] = useState<Selection>(MATRIX_SELECTION);
  const [editingCellId, setEditingCellId] = useState<string | null>(null);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  /**
   * Global (fit-to-screen, read-only) view mode. Per-viewer and per-matrix in
   * localStorage — deliberately NOT in `viewState`, which is server-persisted
   * and shared across every viewer of the matrix (2026-08-25 design doc, S1).
   */
  const globalView = useMatrixViewMode(matrix.id) === 'global';
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('historique');

  const gridRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // ── Notes (issue #19) ────────────────────────────────────────────────────
  const notesQuery = useMatrixNotes(isGenerated ? matrix.id : undefined);
  const noteThreads = notesQuery.data?.threads;
  const noteCounts = openCountsByTarget(noteThreads ?? []);
  const viewer: NoteViewer | null =
    meQuery.data && effectiveRole ? { id: meQuery.data.id, role: effectiveRole } : null;
  const noteMarkersHidden = useNoteMarkersHidden(viewer?.id ?? '', matrix.id);
  /** The bubble anchored to a cell or header while the drawer is closed (or absent, in global view). */
  const [notesPopover, setNotesPopover] = useState<{
    target: NoteTarget;
    anchor: HTMLElement;
    compose: boolean;
  } | null>(null);
  /** Bumped by every « Ajouter une note »: the composer takes the focus. */
  const [composeKey, setComposeKey] = useState(0);
  /** « Voir dans la grille »: the spot to show once the grid is back on screen. */
  const [pendingSeeTarget, setPendingSeeTarget] = useState<NoteTarget | null>(null);
  const notesBubbleRef = useRef<HTMLDivElement>(null);

  // ── Regeneration (2026-08-14 design doc) ────────────────────────────────
  const regenerationBatchQuery = useQuery({
    queryKey: ['regeneration-batch', matrix.id],
    queryFn: () => matrixRegenerationApi.getLatestBatch(matrix.id),
    enabled: isGenerated,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 3000 : false;
    },
  });

  // `tone` distinguishes the regeneration-complete notice from a failed
  // view-state write — the two share one toast slot but must not look alike.
  const [toast, setToast] = useState<{ message: string; tone: 'info' | 'danger' } | null>(null);
  const showToast = useToast();
  const setToastMessage = (message: string | null) =>
    setToast(message === null ? null : { message, tone: 'info' });
  const [regenerationNotice, setRegenerationNotice] = useState<string | null>(null);
  const [recentlyRegeneratedCellIds, setRecentlyRegeneratedCellIds] = useState<Set<string>>(
    new Set(),
  );
  const previousBatchStatusRef = useRef<string | undefined>(undefined);

  // I7/I8 — fires exactly once per batch, the moment it transitions from
  // pending/running to succeeded/failed (not on every poll tick while it
  // stays in that terminal state, and never on the very first load — a
  // batch that already finished before this page mounted is stale history,
  // not a "just happened" event; see I8's ephemeral-on-reload rule).
  // biome-ignore lint/correctness/useExhaustiveDependencies: only regenerationBatchQuery.data should re-trigger this — matrix.id/cellsQuery.data/selection/isDrawerOpen are read at the moment of transition, not watched for their own changes; queryClient/setters are stable.
  useEffect(() => {
    const batch = regenerationBatchQuery.data;
    const previousStatus = previousBatchStatusRef.current;
    previousBatchStatusRef.current = batch?.status;
    const justCompleted =
      (previousStatus === 'pending' || previousStatus === 'running') &&
      (batch?.status === 'succeeded' || batch?.status === 'failed');
    if (!batch || !justCompleted) return;

    if (batch.status === 'succeeded') {
      void queryClient.invalidateQueries({ queryKey: ['cells', matrix.id] });

      const cells = cellsQuery.data ?? [];
      const touchedCells = cellsInScope(scopeSelectorToSelection(batch.scopeSelector), cells);
      const selectedIds = new Set(cellsInScope(selection, cells).map((cell) => cell.id));
      const unselectedTouchedIds = touchedCells
        .filter((cell) => !selectedIds.has(cell.id))
        .map((cell) => cell.id);
      if (unselectedTouchedIds.length > 0) {
        setRecentlyRegeneratedCellIds(
          (previous) => new Set([...previous, ...unselectedTouchedIds]),
        );
      }

      const count = touchedCells.length;
      const message = `Régénération terminée — ${count} cellule${count > 1 ? 's' : ''}.`;
      if (isDrawerOpen) {
        setRegenerationNotice(message);
      } else {
        setToastMessage(message);
      }
    }
  }, [regenerationBatchQuery.data]);

  // The matrix-wide toast (I7) and the failed view-state write are shown by the
  // design system's toast, which dismisses itself; the in-drawer notice below
  // is transient too.
  useEffect(() => {
    if (!toast) return;
    showToast(toast.message, { tone: toast.tone === 'danger' ? 'error' : 'info' });
    setToast(null);
  }, [toast, showToast]);
  useEffect(() => {
    if (!regenerationNotice) return;
    const timer = setTimeout(() => setRegenerationNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [regenerationNotice]);

  function handleDismissRegenerationBadge(cellId: string) {
    setRecentlyRegeneratedCellIds((previous) => {
      if (!previous.has(cellId)) return previous;
      const next = new Set(previous);
      next.delete(cellId);
      return next;
    });
  }

  // ── Add-dimension job (point 14) ────────────────────────────────────────
  // Same "just completed" polling shape as the regeneration batch above —
  // the newly-created dimension row itself shows up immediately (see
  // `addDimensionMutation.onSuccess` below, which patches the `matrix` query
  // cache directly — there is no separate `dimensions` query in this app;
  // `matrix.dimensions` is part of the same `['matrix', matrix.id]` entry
  // `matrixQuery` above already owns), but its 8 cells only materialize once
  // this job succeeds.
  const dimensionJobQuery = useQuery({
    queryKey: ['dimension-generation-job', matrix.id],
    queryFn: () => dimensionGenerationApi.getLatest(matrix.id),
    enabled: isGenerated,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 3000 : false;
    },
  });

  const previousDimensionJobStatusRef = useRef<string | undefined>(undefined);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only dimensionJobQuery.data should re-trigger this — matrix.id/queryClient are stable.
  useEffect(() => {
    const job = dimensionJobQuery.data;
    const previousStatus = previousDimensionJobStatusRef.current;
    previousDimensionJobStatusRef.current = job?.status;
    const justCompleted =
      (previousStatus === 'pending' || previousStatus === 'running') &&
      (job?.status === 'succeeded' || job?.status === 'failed');
    if (!job || !justCompleted) return;
    if (job.status === 'succeeded') {
      void queryClient.invalidateQueries({ queryKey: ['cells', matrix.id] });
    }
  }, [dimensionJobQuery.data]);

  const addDimensionMutation = useMutation({
    mutationFn: (input: { title: string; comment: string }) =>
      dimensionGenerationApi.create(matrix.id, input),
    onSuccess: ({ job, dimension }) => {
      queryClient.setQueryData(['dimension-generation-job', matrix.id], job);
      // Shows the new (pending-cells) row immediately — appended straight
      // into the `matrix` cache rather than an invalidate/refetch, since the
      // response already carries the full new `Dimension`.
      queryClient.setQueryData<MatrixWithDimensions>(['matrix', matrix.id], (previous) =>
        previous ? { ...previous, dimensions: [...previous.dimensions, dimension] } : previous,
      );
    },
  });

  // Reset automatically on the next `.mutate()` call — a retry from the
  // still-open popover clears the stale message on its own.
  const addDimensionSubmitError = addDimensionMutation.isError
    ? toDimensionSubmitError(addDimensionMutation.error)
    : null;

  const retryDimensionJobMutation = useMutation({
    mutationFn: (jobId: string) => dimensionGenerationApi.retry(jobId),
    onSuccess: (job) => {
      queryClient.setQueryData(['dimension-generation-job', matrix.id], job);
    },
  });

  function handleAddDimension(input: { title: string; comment: string }) {
    addDimensionMutation.mutate(input);
  }

  function handleRetryDimensionJob(jobId: string) {
    retryDimensionJobMutation.mutate(jobId);
  }

  // Outside-click reset (decision D7): clicking anywhere outside both the
  // grid and the drawer resets selection to the whole-matrix default. This
  // is a genuine DOM-event side effect, not data fetching, so a `useEffect`
  // registering/cleaning up the listener is the right tool here.
  //
  // Suspended while multi-select is on (M11): there, the selection is an
  // accumulating set whose whole point is to survive clicks elsewhere on the
  // page (the floating action bar itself sits outside the grid). Turning the
  // mode off is the way to clear it.
  useEffect(() => {
    if (multiSelectMode) return;
    function handleDocumentClick(event: MouseEvent) {
      const target = event.target as Node;
      // A control that left the page while handling this very click (« Voir dans la
      // grille » closes the Notes view it sits in) is not a click elsewhere.
      if (!target.isConnected) return;
      if (gridRef.current?.contains(target)) return;
      if (drawerRef.current?.contains(target)) return;
      // The notes bubble lives in a portal: a click in it is not « elsewhere ».
      if (notesBubbleRef.current?.contains(target)) return;
      setSelection(MATRIX_SELECTION);
    }
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [multiSelectMode]);

  // The bubble belongs to the grid on screen: another view or global view closes it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the flags are the triggers, the setter is stable.
  useEffect(() => {
    setNotesPopover(null);
  }, [viewOpen, globalView]);

  // « Voir dans la grille »: the grid is on screen again, so its cells have a position to anchor to.
  useEffect(() => {
    if (pendingSeeTarget === null || viewOpen) return;
    const target = pendingSeeTarget;
    setPendingSeeTarget(null);
    const anchor = findNoteAnchor(gridRef.current, target);
    if (anchor && typeof anchor.scrollIntoView === 'function') {
      anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    openNotesFor(target, anchor, false);
  }, [pendingSeeTarget, viewOpen]);

  function updateMatrixCache(updated: Matrix) {
    queryClient.setQueryData<MatrixWithDimensions>(['matrix', matrix.id], (previous) =>
      previous ? { ...previous, ...updated } : previous,
    );
    // Every mutation routed through here (resize / visibility / undo / redo)
    // also writes to — or moves the cursor within — the actions log that the
    // drawer's Historique "Global" scope renders, so that query has to be
    // invalidated too. Patching the matrix cache alone leaves the panel
    // showing a stale log (or an empty state) until a manual refresh.
    void queryClient.invalidateQueries({ queryKey: ['actions', matrix.id] });
  }

  const saveCellMutation = useMutation({
    mutationFn: ({ cellId, content }: { cellId: string; content: string }) =>
      cellsApi.createVersion(cellId, { content }),
    onSuccess: (updatedCell) => {
      queryClient.setQueryData<Cell[]>(['cells', matrix.id], (previous) =>
        previous?.map((cell) => (cell.id === updatedCell.id ? updatedCell : cell)),
      );
    },
  });

  const resizeMutation = useMutation({
    mutationFn: (input: PerformResizeInput) => matricesApi.performResize(matrix.id, input),
    onSuccess: updateMatrixCache,
  });
  const visibilityMutation = useMutation({
    mutationFn: (input: PerformVisibilityInput) => matricesApi.performVisibility(matrix.id, input),
    onSuccess: updateMatrixCache,
  });
  const undoMutation = useMutation({
    mutationFn: () => matricesApi.undo(matrix.id),
    onSuccess: updateMatrixCache,
  });
  const redoMutation = useMutation({
    mutationFn: () => matricesApi.redo(matrix.id),
    onSuccess: updateMatrixCache,
  });
  // Zoom writes `viewState` through its own `PATCH /matrices/:id/view-state`
  // — it's explicitly not part of the undo/redo system (E13). "Réinitialiser
  // les largeurs/hauteurs" used to go through this same path (a session-3 gap:
  // individual drag-resizes were undoable, the bulk clear wasn't); session 5
  // moved it onto `resizeMutation` (`target: 'all'`, see `handleResetSizes`)
  // so it's action-logged like every other resize.
  //
  // It used to ride on the generic `PATCH /matrices/:id`, whose brouillon-only
  // guard rejected every single zoom click with a 409 — this page only ever
  // renders a `généré` matrix. With no `onError` here, that failed silently
  // and zoom never worked at all; hence both the dedicated route and the
  // error surface below.
  const patchViewStateMutation = useMutation({
    mutationFn: (viewState: MatrixViewStateShape) =>
      matricesApi.patchViewState(matrix.id, { viewState }),
    onSuccess: updateMatrixCache,
    onError: () => setToast({ message: VIEW_STATE_ERROR_MESSAGE, tone: 'danger' }),
  });

  const viewState = matrix.viewState ?? DEFAULT_MATRIX_VIEW_STATE;

  /**
   * Cells whose current version predates the last context change (D25).
   * Empty once the banner has been dismissed for this change (D26), which is
   * what makes dismissing the banner retire the per-cell markers too — the
   * grid is told nothing is stale rather than being told to hide anything.
   */
  const staleIds = staleCellIds(
    // Not yet loaded means nothing is known to be stale — the banner and the
    // markers simply stay away until the cells arrive.
    cellsQuery.data ?? [],
    matrix.contextUpdatedAt,
    viewState.dismissedContextChangeAt,
  );

  /**
   * Persisting the dismissal records WHICH change was dismissed, not merely
   * that one was — so the banner returns on the next context change without
   * needing to be re-dismissed forever (D26).
   */
  function handleDismissContextChange() {
    if (matrix.contextUpdatedAt === null) return;
    patchViewStateMutation.mutate({
      ...viewState,
      dismissedContextChangeAt: matrix.contextUpdatedAt,
    });
  }

  /**
   * "Afficher les cellules concernées" reuses the existing multi-select scope
   * rather than inventing a highlight of its own: the grid already renders
   * that set prominently, and it leaves the affected cells selected so the
   * drawer's Régénérer tab can act on exactly them.
   */
  function handleShowAffectedCells() {
    if (staleIds.length === 0) return;
    setSelection({ scope: 'cells', cellIds: [...staleIds] });
  }

  function handleUndo() {
    if (!matrix.canUndo) return;
    undoMutation.mutate();
  }

  function handleRedo() {
    if (!matrix.canRedo) return;
    redoMutation.mutate();
  }

  // Keyboard shortcuts (E17): Ctrl+Z / Ctrl+Shift+Z (and the Mac Cmd
  // equivalent) mirror the toolbar's undo/redo buttons, except while an
  // editable text control is focused — there, native browser text-undo
  // must win instead. See `keyboard-shortcuts.ts` for the pure guard/mapping
  // logic (unit-tested there — mounting this route in isolation needs a
  // live router context, which the project's existing convention already
  // treats as impractical, see `vitest.config.ts`).
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (isEditableElement(document.activeElement)) return;
      const shortcut = matchUndoRedoShortcut(event);
      if (shortcut === 'undo' && matrix.canUndo) {
        event.preventDefault();
        undoMutation.mutate();
      } else if (shortcut === 'redo' && matrix.canRedo) {
        event.preventDefault();
        redoMutation.mutate();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [matrix.canUndo, matrix.canRedo, undoMutation, redoMutation]);

  function handleZoomIn() {
    patchViewStateMutation.mutate({
      ...viewState,
      zoomStep: clampZoomStep(viewState.zoomStep + 1) satisfies ZoomStep,
    });
  }

  function handleZoomOut() {
    patchViewStateMutation.mutate({
      ...viewState,
      zoomStep: clampZoomStep(viewState.zoomStep - 1) satisfies ZoomStep,
    });
  }

  function handleShowAll() {
    visibilityMutation.mutate({ target: 'all', ref: null });
  }

  /**
   * M13 — clears every custom width/height in one action. Routed through
   * `resizeMutation` (`target: 'all'`, session 5) rather than the plain
   * viewState patch, so it's action-logged and Ctrl+Z reverts it — the same
   * guarantee individual drag-resizes already had.
   */
  function handleResetSizes() {
    resizeMutation.mutate({ target: 'all' });
  }

  function handleResize(target: 'column' | 'row', ref: string, sizePx: number) {
    resizeMutation.mutate({ target, ref, sizePx });
  }

  function handleToggleVisibility(
    target: 'activation' | 'column' | 'dimension',
    ref: string | null,
    hidden: boolean,
  ) {
    visibilityMutation.mutate({ target, ref, hidden });
  }

  function handleSelect(next: Selection) {
    setEditingCellId(null);
    setSelection(next);
  }

  function handleStartEdit(cellId: string) {
    setEditingCellId(cellId);
  }

  function handleSaveEdit(cellId: string, content: string) {
    setEditingCellId(null);
    saveCellMutation.mutate({ cellId, content });
  }

  /** Escape while editing (point 9) — leave edit mode without persisting anything. */
  function handleCancelEdit(_cellId: string) {
    setEditingCellId(null);
  }

  /** A grid "..." menu item (M7): re-scope, switch tab, and force the drawer open. */
  function handleDrawerRequest(request: DrawerRequest) {
    setEditingCellId(null);
    setSelection(request.selection);
    setDrawerTab(request.tab);
    setIsDrawerOpen(true);
    setNotesPopover(null);
  }

  /** Leaving multi-select mode drops the accumulated set (M11). */
  function handleToggleMultiSelect() {
    setEditingCellId(null);
    setMultiSelectMode((previous) => {
      if (previous) setSelection(MATRIX_SELECTION);
      return !previous;
    });
  }

  /**
   * Entering or leaving global view drops any in-progress edit and leaves
   * multi-select, exactly as `handleToggleMultiSelect` does — matching the
   * existing toggle's behaviour rather than inventing a second convention
   * (2026-08-25 design doc, R9). Note that this discards unsaved edit content:
   * unmounting the `contentEditable` does not reliably fire its `onBlur` save.
   *
   * Note what is deliberately NOT touched: `viewState.zoomStep` and
   * `isDrawerOpen`. Leaving both alone is what makes leaving the mode restore
   * the user's previous zoom and drawer state, with no save/restore bookkeeping
   * and no way for the two to drift apart.
   */
  function handleToggleGlobalView() {
    setEditingCellId(null);
    setMultiSelectMode(false);
    setSelection(MATRIX_SELECTION);
    setNotesPopover(null);
    setMatrixViewMode(matrix.id, globalView ? 'normal' : 'global');
  }

  /** The floating bar's action (M11) — same destination as any "..." → régénérer. */
  function handleRegenerateSelection() {
    setDrawerTab('regen');
    setIsDrawerOpen(true);
    setNotesPopover(null);
  }

  /** The drawer opening takes the place of the notes bubble. */
  function handleDrawerOpenChange(open: boolean) {
    setIsDrawerOpen(open);
    if (open) setNotesPopover(null);
  }

  /**
   * Toolbar's "Cadrage" (issue #3), and the staleness banner's "Voir le
   * cadrage →". A search-param navigation on this same route: the component
   * never unmounts, so every piece of grid state survives the round trip.
   *
   * `replace: true` keeps the browser's back button meaning "leave this
   * matrix" rather than "undo the last few toggles" — this is a view switch,
   * not a place.
   */
  function setCadrageOpen(open: boolean) {
    void navigate({
      to: '/matrices/$matrixId',
      params: { matrixId: matrix.id },
      search: open ? { vue: 'cadrage' } : {},
      replace: true,
    });
  }

  /**
   * Toolbar's « Notes » (issue #19): `?vue=notes`, a view switch like Cadrage,
   * replacing the history entry for the same reason.
   */
  function setNotesViewOpen(open: boolean) {
    void navigate({
      to: '/matrices/$matrixId',
      params: { matrixId: matrix.id },
      search: open ? { vue: 'notes' } : {},
      replace: true,
    });
  }

  /** The Notes view puts its filters, sort and presentation in the URL. */
  function handleNotesSearchChange(next: NotesViewSearch) {
    void navigate({
      to: '/matrices/$matrixId',
      params: { matrixId: matrix.id },
      search: { vue: 'notes', ...next },
      replace: true,
    });
  }

  /**
   * Shows the notes of a spot (issue #19). With the drawer open (and on
   * screen) it selects the spot and switches the drawer to its Notes tab;
   * otherwise it opens the bubble beside `anchor`, selecting the spot too so
   * the grid shows which one it is about. A spot with nothing to anchor to (the
   * matrix itself, a hidden column) goes to the drawer.
   */
  function openNotesFor(target: NoteTarget, anchor: HTMLElement | null, compose: boolean) {
    const nextSelection = noteTargetToSelection(target, cellsQuery.data ?? []);
    if (!globalView && !multiSelectMode && nextSelection) {
      setEditingCellId(null);
      setSelection(nextSelection);
    }
    const drawerOnScreen = isDrawerOpen && !globalView && !viewOpen;
    if (drawerOnScreen || anchor === null) {
      setNotesPopover(null);
      setDrawerTab('notes');
      setIsDrawerOpen(true);
    } else if (
      !compose &&
      notesPopover !== null &&
      notesPopover.anchor === anchor &&
      !notesPopover.compose &&
      targetKey(notesPopover.target) === targetKey(target)
    ) {
      // The marker that opened the bubble closes it.
      setNotesPopover(null);
      return;
    } else {
      setNotesPopover({ target, anchor, compose });
    }
    if (compose) setComposeKey((key) => key + 1);
  }

  function handleNotesRequest(request: NotesRequest) {
    openNotesFor(request.target, request.anchor, request.compose);
  }

  /** « Voir dans la grille »: back to the grid, then its notes once it is on screen. */
  function handleSeeInGrid(target: NoteTarget) {
    setNotesViewOpen(false);
    setPendingSeeTarget(target);
  }

  /** Toolbar's "Exporter" (2026-08-09 design doc) — a real navigation now, not the old M13 inert shell. */
  function handleExport() {
    void navigate({ to: '/matrices/$matrixId/export', params: { matrixId: matrix.id } });
  }

  const createMatrixMutation = useMutation({
    mutationFn: () => matricesApi.create({ companyId: matrix.companyId }),
    onSuccess: (created) => {
      void navigate({ to: '/wizard/$matrixId', params: { matrixId: created.id } });
    },
  });

  function handleCreateMatrix() {
    createMatrixMutation.mutate();
  }

  // « Nouvelle matrice » → « Depuis cette matrice » (issue #16, planche 08).
  // Its own instance, apart from Cadrage's link, so a failure is shown next
  // to the control that caused it.
  const toolbarDerive = useDeriveMatrix();
  const displayState = matrixDisplayState(matrix);
  const canDeriveFromThis = displayState === 'terminee' || displayState === 'brouillon';

  if (!isGenerated) {
    return <PendingScreen />;
  }

  if (!companyQuery.data || !cellsQuery.data) {
    return <PendingScreen />;
  }

  const selectedCellCount = selection.scope === 'cells' ? selection.cellIds.length : 0;

  // ── Notes (issue #19) ────────────────────────────────────────────────────
  const notesContext = {
    singularitySetup: companyQuery.data.singularitySetup,
    dimensions: matrix.dimensions,
    paliers: matrix.paliers,
  };
  const notesScope: NotesScope = { type: 'matrix', matrixId: matrix.id };
  const selectionNoteTarget = selectionToNoteTarget(selection, cellsQuery.data);
  const gridNotes: GridNotes | undefined = viewer
    ? {
        counts: noteCounts,
        latest: latestOpenByTarget(noteThreads ?? []),
        markersVisible: !noteMarkersHidden,
        onRequest: handleNotesRequest,
      }
    : undefined;
  const drawerNotes: DrawerNotes | undefined = viewer
    ? {
        target: selectionNoteTarget,
        targetLabel: selectionNoteTarget ? noteTargetLabel(selectionNoteTarget, notesContext) : '',
        count: selectionNoteTarget ? (noteCounts.get(targetKey(selectionNoteTarget)) ?? 0) : 0,
        scope: notesScope,
        companyId: matrix.companyId,
        companyName: companyQuery.data.name,
        viewer,
        focusComposerKey: composeKey,
        onOpenAllNotes: () => setNotesViewOpen(true),
      }
    : undefined;
  const bubbleVisible = notesPopover !== null && !viewOpen && (globalView || !isDrawerOpen);

  return (
    // The drawer is a sibling of the *entire* content column (chrome + grid)
    // rather than just the grid row, so it spans the whole page height, not
    // only the space below the toolbar.
    <div className="flex h-full min-h-0">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* No title chrome here (point 1) — BUILD-770's centralized <Breadcrumbs>
            (rendered from __root.tsx) is the only place the matrix name shows;
            its own MatrixBreadcrumb leaf crumb (components/breadcrumbs.tsx)
            owns the inline rename affordance. */}

        <MatrixToolbar
          canUndo={matrix.canUndo}
          canRedo={matrix.canRedo}
          viewState={viewState}
          multiSelectMode={multiSelectMode}
          globalView={globalView}
          onToggleGlobalView={handleToggleGlobalView}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onShowAll={handleShowAll}
          onResetSizes={handleResetSizes}
          onToggleMultiSelect={handleToggleMultiSelect}
          cadrageOpen={cadrageOpen}
          onToggleCadrage={() => setCadrageOpen(!cadrageOpen)}
          {...(viewer
            ? {
                notes: {
                  open: notesViewOpen,
                  openCount: totalOpenCount(noteCounts),
                  onToggle: () => setNotesViewOpen(!notesViewOpen),
                  markersHidden: noteMarkersHidden,
                  onToggleMarkers: () =>
                    setNoteMarkersHidden(viewer.id, matrix.id, !noteMarkersHidden),
                },
              }
            : {})}
          onExport={handleExport}
          onCreateMatrix={handleCreateMatrix}
          canDeriveFromThis={canDeriveFromThis}
          onDeriveFromThis={() => toolbarDerive.deriveMatrix(matrix.id)}
          deriveError={toolbarDerive.errorMessage}
        />
        {/* Advisory, never blocking: it sits above the grid and renders
            nothing at all when no cell is stale. Hidden in Cadrage, where its
            two controls ("afficher les cellules", "voir le cadrage") both point
            at things that are not on screen. */}
        {staleIds.length > 0 && !viewOpen && (
          <div className="px-4 pb-2">
            <ContextStaleBanner
              affectedCellCount={staleIds.length}
              onShowAffected={handleShowAffectedCells}
              onDismiss={handleDismissContextChange}
              isDismissing={patchViewStateMutation.isPending}
              onOpenCadrage={() => setCadrageOpen(true)}
            />
          </div>
        )}

        {/* Mounted, not rendered: `hidden` rather than a conditional, exactly
            as `matrix-wizard.tsx` keeps its four steps alive. Unmounting
            `MatrixGrid` would discard the zoom, the selection and the scroll
            position on every visit to Cadrage and back — which is the round
            trip this feature is *for*. Its state is not lifted anywhere
            either; the component simply keeps holding it. */}
        <div
          className={cn('relative min-h-0 flex-1 overflow-hidden', viewOpen ? 'hidden' : 'flex')}
        >
          <div className="min-w-0 flex-1 overflow-hidden">
            <MatrixGrid
              ref={gridRef}
              cells={cellsQuery.data}
              dimensions={matrix.dimensions}
              singularitySetup={companyQuery.data.singularitySetup}
              paliers={matrix.paliers}
              noyau={companyQuery.data.noyau}
              selection={selection}
              onSelect={handleSelect}
              editingCellId={editingCellId}
              onStartEdit={handleStartEdit}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              viewState={viewState}
              staleCellIds={staleIds}
              globalView={globalView}
              onResize={handleResize}
              onToggleVisibility={handleToggleVisibility}
              onDrawerRequest={handleDrawerRequest}
              multiSelectMode={multiSelectMode}
              recentlyRegeneratedCellIds={recentlyRegeneratedCellIds}
              onDismissRegenerationBadge={handleDismissRegenerationBadge}
              dimensionJob={dimensionJobQuery.data ?? null}
              onAddDimension={handleAddDimension}
              isAddingDimension={addDimensionMutation.isPending}
              addDimensionError={addDimensionSubmitError}
              onRetryDimensionJob={handleRetryDimensionJob}
              isRetryingDimensionJob={retryDimensionJobMutation.isPending}
              {...(gridNotes ? { notes: gridNotes } : {})}
            />
          </div>

          {/* Multi-select floating bar (M11, mockup Écran 23). */}
          {selectedCellCount > 0 && (
            <div className="-translate-x-1/2 absolute bottom-4 left-1/2 z-10 flex items-center gap-3 rounded-md border border-gray-200 bg-white py-2 pr-2 pl-4 text-noir shadow-lg">
              <span className="font-semibold text-caption">
                {selectedCellCount} cellule{selectedCellCount > 1 ? 's' : ''} sélectionnée
                {selectedCellCount > 1 ? 's' : ''}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon="refresh"
                onClick={handleRegenerateSelection}
              >
                régénérer la sélection
              </Button>
            </div>
          )}
        </div>

        {/* The Cadrage tab, in the space the grid just vacated. Mounted only
            while showing — unlike the grid, it holds nothing worth preserving
            across a toggle (its drafts are seeded from the matrix query, and
            an unsaved one must not survive a trip through the grid and come
            back looking saved). */}
        {cadrageOpen && (
          <CadragePanel
            matrix={matrix}
            company={companyQuery.data}
            role={effectiveRole ?? 'user'}
          />
        )}

        {/* The Notes view (issue #19): like Cadrage, in the space the grid vacated. */}
        {notesViewOpen && viewer && (
          <NotesView
            matrixId={matrix.id}
            companyId={matrix.companyId}
            companyName={companyQuery.data.name}
            viewer={viewer}
            singularitySetup={companyQuery.data.singularitySetup}
            dimensions={matrix.dimensions}
            search={search}
            onSearchChange={handleNotesSearchChange}
            onSeeInGrid={handleSeeInGrid}
          />
        )}
      </div>

      {/* Absent in global view, rail included — not merely closed (design doc
          F2). `isDrawerOpen` is never written here, so unmounting means the
          user's previous drawer state restores itself on leaving the mode.
          Absent in Cadrage for the same reason: both its tabs are scoped to
          the grid selection, and the grid is not on screen. */}
      {!globalView && !viewOpen && (
        <MatrixDrawer
          ref={drawerRef}
          matrixId={matrix.id}
          selection={selection}
          cells={cellsQuery.data}
          dimensions={matrix.dimensions}
          singularitySetup={companyQuery.data.singularitySetup}
          paliers={matrix.paliers}
          isOpen={isDrawerOpen}
          onOpenChange={handleDrawerOpenChange}
          activeTab={drawerTab}
          onTabChange={setDrawerTab}
          latestRegenerationBatch={regenerationBatchQuery.data ?? null}
          regenerationNotice={regenerationNotice}
          role={effectiveRole ?? 'user'}
          {...(drawerNotes ? { notes: drawerNotes } : {})}
        />
      )}

      {/* Tiroir fermé (or absent, in global view): the bubble beside the cell, in a portal. */}
      {bubbleVisible && notesPopover && viewer && (
        <NotesBubble
          anchor={notesPopover.anchor}
          label={`Notes - ${noteTargetLabel(notesPopover.target, notesContext)}`}
          onClose={() => setNotesPopover(null)}
          containerRef={notesBubbleRef}
        >
          <NotesTargetPanel
            scope={notesScope}
            target={notesPopover.target}
            targetLabel={noteTargetLabel(notesPopover.target, notesContext)}
            companyId={matrix.companyId}
            companyName={companyQuery.data.name}
            viewer={viewer}
            allowNewNote={!globalView}
            {...(notesPopover.compose ? { focusComposerKey: composeKey } : {})}
          />
        </NotesBubble>
      )}
    </div>
  );
}

function PendingScreen() {
  return <p className="text-gray-500 text-small">Chargement…</p>;
}

function ErrorScreen() {
  return (
    <section className="space-y-2">
      <h1 className="font-bold text-danger text-h3">Erreur</h1>
      <p className="text-small">Impossible de contacter le serveur. Réessayez plus tard.</p>
    </section>
  );
}
